// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: mail hidden from Chats (JP 2026-10-07). A rule hides inbox mail from
// every chat list (Chats, the Rail, Home) by the sender's address or by a
// phrase in the subject; Mail itself still lists it. The rules live in the
// synced calendar prefs blob (`mail_chat_hide`), so phone and desktop match.
// Pure helpers only (no Lingui, so stores can import them); MailChatHide
// (state) wires them to the stores and MailChatHideMenu labels them. The limits
// mirror konvo_api KonvoMailChatHidePrefs.ts.

export type MailChatHideKind = 'sender' | 'subject';

export interface MailChatHideRule {
	kind: MailChatHideKind;
	value: string;
}

export interface MailChatHideTarget {
	from_address: string | null;
	subject: string;
}

export const MAIL_CHAT_HIDE_MAX_RULES = 200;
export const MAIL_CHAT_HIDE_MAX_VALUE = 200;
/** Shorter suggestions than this would hide far too much; the whole subject is offered instead. */
const MIN_PHRASE_CHARS = 4;
const MAX_PHRASE_CHARS = 60;

/** Trimmed, sender lower-cased, 1–200 characters; null when nothing usable is left. */
export function normalizeRule(rule: {kind: unknown; value: unknown}): MailChatHideRule | null {
	if (rule.kind !== 'sender' && rule.kind !== 'subject') return null;
	if (typeof rule.value !== 'string') return null;
	const trimmed = rule.value.trim();
	if (trimmed.length === 0 || trimmed.length > MAIL_CHAT_HIDE_MAX_VALUE) return null;
	return {kind: rule.kind, value: rule.kind === 'sender' ? trimmed.toLowerCase() : trimmed};
}

function ruleKey(rule: MailChatHideRule): string {
	return `${rule.kind}:${rule.value.toLowerCase()}`;
}

export function sameRule(a: MailChatHideRule, b: MailChatHideRule): boolean {
	return ruleKey(a) === ruleKey(b);
}

/** The server's sanitising, so a fresh device and the server agree: valid, unique, at most 200. */
export function sanitizeRules(raw: unknown): Array<MailChatHideRule> {
	if (!Array.isArray(raw)) return [];
	const rules: Array<MailChatHideRule> = [];
	const seen = new Set<string>();
	for (const entry of raw) {
		if (rules.length >= MAIL_CHAT_HIDE_MAX_RULES) break;
		if (entry == null || typeof entry !== 'object' || Array.isArray(entry)) continue;
		const rule = normalizeRule(entry as {kind: unknown; value: unknown});
		if (!rule || seen.has(ruleKey(rule))) continue;
		seen.add(ruleKey(rule));
		rules.push(rule);
	}
	return rules;
}

/** Sender: the exact address, any case. Subject: the phrase anywhere in it, any case. */
export function hiddenFromChats(message: MailChatHideTarget, rules: ReadonlyArray<MailChatHideRule>): boolean {
	if (rules.length === 0) return false;
	const address = message.from_address?.trim().toLowerCase() ?? '';
	const subject = message.subject.toLowerCase();
	for (const rule of rules) {
		if (rule.kind === 'sender') {
			if (address && address === rule.value.toLowerCase()) return true;
		} else if (subject.includes(rule.value.toLowerCase())) {
			return true;
		}
	}
	return false;
}

/** The mails a chat list shows: the same array when no rule applies. */
export function visibleInChats<T extends MailChatHideTarget>(
	messages: ReadonlyArray<T>,
	rules: ReadonlyArray<MailChatHideRule>,
): ReadonlyArray<T> {
	if (rules.length === 0) return messages;
	const visible = messages.filter((message) => !hiddenFromChats(message, rules));
	return visible.length === messages.length ? messages : visible;
}

const LEADING_TAG = /^\s*\[[^\]]*\]\s*/;
const LEADING_REPLY = /^\s*(?:re|fwd?|aw|wg)\s*:\s*/i;

/** The subject without leading `[repo]`-style tags and Re:/Fw: prefixes. */
function cleanSubject(subject: string): string {
	let rest = subject.replace(/\s+/g, ' ').trim();
	for (;;) {
		const next = rest.replace(LEADING_TAG, '').replace(LEADING_REPLY, '');
		if (next === rest) return rest.trim();
		rest = next;
	}
}

/** At most 60 characters, cut at a word boundary when one is not too far back. */
function clip(text: string): string {
	if (text.length <= MAX_PHRASE_CHARS) return text.trim();
	const head = text.slice(0, MAX_PHRASE_CHARS + 1);
	const space = head.lastIndexOf(' ');
	return (space >= MAX_PHRASE_CHARS / 2 ? head.slice(0, space) : head.slice(0, MAX_PHRASE_CHARS)).trim();
}

/**
 * The phrase Konvo offers for "Subjects containing …": the subject without tags
 * like `[Nuvio-Software/konvo]` or Re:/Fw: prefixes, up to its first ":".
 * "[Nuvio-Software/konvo] PR run failed: Tests - main" gives "PR run failed".
 * A phrase under 4 characters falls back to the cleaned subject (60 characters
 * at most); empty when even that is under 4, since it would hide far too much.
 */
export function suggestSubjectPhrase(subject: string | null | undefined): string {
	const cleaned = cleanSubject(subject ?? '');
	const phrase = clip(cleaned.split(':')[0] ?? '');
	if (phrase.length >= MIN_PHRASE_CHARS) return phrase;
	const whole = clip(cleaned);
	return whole.length >= MIN_PHRASE_CHARS ? whole : '';
}

/** The rules a mail row offers: its sender, and the suggested subject phrase when there is one. */
export function rulesOfferedFor(message: MailChatHideTarget): Array<MailChatHideRule> {
	const offered: Array<MailChatHideRule> = [];
	const sender = normalizeRule({kind: 'sender', value: message.from_address ?? ''});
	if (sender) offered.push(sender);
	const subject = normalizeRule({kind: 'subject', value: suggestSubjectPhrase(message.subject)});
	if (subject) offered.push(subject);
	return offered;
}
